import { useQuery } from "@tanstack/react-query";
import { createContext, ReactNode, useContext, useMemo } from "react";
import { useAuthContext } from "../Auth";
import { instance } from "../../api/apiBase";
import { useIsBoomScreenOnly } from "shared/app/BoomCrmPaywall/hooks";
import {
  CRM_ONLY_NOTIFICATION_TYPES,
  type Notification,
  type NotificationType
} from "views/boom-notifications/types";

const NotificationContext = createContext<{
  newestNotifications: Notification[] | undefined;
  totalUnread: number;
  refetchTotalUnreadNotification: () => void;
}>({
  newestNotifications: [],
  totalUnread: 0,
  refetchTotalUnreadNotification: () => {}
});

const notificationApi = {
  getNewest: () =>
    instance.get("/portal/v1/notifications/newest").then((res) => res.data),
  getTotalUnread: () =>
    instance
      .get("/portal/v1/notifications/unread_count")
      .then((res) => res.data?.total_unread || 0)
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { auth } = useAuthContext();
  const isBoomScreenOnly = useIsBoomScreenOnly();

  const { data: totalUnread, refetch: refetchTotalUnreadNotification } =
    useQuery({
      queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"],
      queryFn: () => notificationApi.getTotalUnread(),
      enabled: Boolean(auth)
    });

  const { data: newestNotifications } = useQuery<Notification[]>({
    queryKey: ["NEWEST_NOTIFICATIONS"],
    queryFn: async () => {
      const newestNoti = await notificationApi.getNewest();
      if ((newestNotifications || []).length) refetchTotalUnreadNotification();
      return newestNoti;
    },
    refetchInterval: 30000,
    enabled: Boolean(auth)
  });

  // CRM-564: BoomScreen-only partners shouldn't see notifications tied to
  // gated Tasks/Leads surfaces. Filter the toast feed; for the total badge,
  // approximate from the filtered newest list (the unread-count endpoint
  // returns CRM-inclusive totals).
  const visibleNewestNotifications = useMemo(() => {
    if (!isBoomScreenOnly) return newestNotifications;
    return (newestNotifications || []).filter(
      (n) => !CRM_ONLY_NOTIFICATION_TYPES.has(n.type as NotificationType)
    );
  }, [isBoomScreenOnly, newestNotifications]);

  const effectiveTotalUnread = isBoomScreenOnly
    ? (visibleNewestNotifications || []).filter((n) => !n.read_at).length
    : totalUnread;

  return (
    <NotificationContext.Provider
      value={{
        newestNotifications: visibleNewestNotifications,
        totalUnread: effectiveTotalUnread,
        refetchTotalUnreadNotification
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotificationContext() {
  return useContext(NotificationContext);
}
